import { type APIPaginatedResponse, EndpointService } from "@/types/api/api";
import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import {
  RedeemableClassification,
  type RedeemablesTypes,
} from "@/components/screens/Redeemables/Shared/Types/ListRedeemablesTypes";
import {
  ConfigurationsService,
  type IConfigurationsService,
} from "./Configurations";
import {
  type IVoucher,
  type IVoucherService,
  VoucherService,
} from "./Vouchers";
import { type ITab, type ITabService, TabService } from "./Tabs";
import {
  DiscountService,
  type IDiscount,
  type IDiscountService,
} from "./Discount";
import { type IPackagesService, PackagesService } from "./Packages";
import {
  CodeGenerationService,
  type ICodeGenerationService,
} from "./CodeGeneration";
import type { ValidDateFilter } from "@/components/screens/Redeemables/Redeemable/List/Types/ListRedeemablesTypes";

export interface IMenuItemRestriction {
  error_message: string;
  menu_items: Array<{
    id: number;
  }>;
  id: number;
  redemption_count: number;
  redemption_limit: number | string;
  title: string;
}
export interface IListRedeemable {
  active: boolean;
  redeemable_type: RedeemablesTypes;
  id: number;
  start_date: string;
  message: string;
  configuration_id: number;
  end_date: string;
  code: string;
}

export enum BulkSendEmailStatus {
  Sent = "sent",
  Failed = "failed",
  Skipped = "skipped",
  NotFound = "not_found",
}

/**
 * Interface representing the request body for the bulk send email endpoint.
 * @interface IBulkSendEmailParams
 * @property {Array<number>} ids — Ids of the Voucher or Prepaid Tab codes to email. Max 200, no duplicates.
 */
export interface IBulkSendEmailParams {
  ids: Array<number>;
}

/**
 * Interface representing a per-code outcome from the bulk send email endpoint.
 * @interface IBulkSendEmailResult
 * @property {number} id — Id of the code this outcome relates to.
 * @property {BulkSendEmailStatus} status — Outcome status for the code.
 * @property {string} [reason] — Optional human-readable reason for skipped codes.
 */
export interface IBulkSendEmailResult {
  id: number;
  status: BulkSendEmailStatus;
  reason?: string;
}

export interface IListRedeemablesParams {
  search?: string;
  date_range?: ValidDateFilter;
  configuration_id?: number;
  active?: boolean;
  redeemable_type?: Array<RedeemablesTypes>;
  page?: number;
  page_size?: number;
  order_by?: string;
}

export interface IListRedeemables
  extends APIPaginatedResponse<IListRedeemable> {}

export interface IPartialUpdateRedeemableParams {
  id: number;
  active: boolean;
  redeemable_classification: RedeemableClassification;
}

export interface IDeleteRedeemableParams {
  id: number;
  redeemable_classification: RedeemableClassification;
}

export interface IExportRedeemablesParams extends IListRedeemablesParams {}

export interface IRedeemablesService {
  configurations: IConfigurationsService;
  discounts: IDiscountService;
  vouchers: IVoucherService;
  tabs: ITabService;
  packages: IPackagesService;
  codeGeneration: ICodeGenerationService;

  list: (
    params?: IListRedeemablesParams,
  ) => UseQueryResult<IListRedeemables, Error>;
  partialUpdateCode: (
    listParams?: IListRedeemablesParams,
  ) => UseMutationResult<
    IDiscount | IVoucher | ITab,
    Error,
    IPartialUpdateRedeemableParams
  >;
  deleteCode: (
    listParams?: IListRedeemablesParams,
  ) => UseMutationResult<boolean, Error, IDeleteRedeemableParams>;
  exportCodesAsCsv: ({
    listParams,
    enabled,
  }: {
    listParams: IListRedeemablesParams;
    enabled: boolean;
  }) => UseQueryResult<string, Error>;
  bulkSendEmail: () => UseMutationResult<
    Array<IBulkSendEmailResult>,
    Error,
    IBulkSendEmailParams
  >;
}

export class RedeemablesService implements IRedeemablesService {
  api: IAPI;
  configurations: IConfigurationsService;
  discounts: IDiscountService;
  vouchers: IVoucherService;
  tabs: ITabService;
  packages: IPackagesService;
  codeGeneration: ICodeGenerationService;

  constructor(api) {
    this.api = api;
    this.configurations = new ConfigurationsService(api);
    this.discounts = new DiscountService(api);
    this.vouchers = new VoucherService(api);
    this.tabs = new TabService(api);
    this.packages = new PackagesService(api);
    this.codeGeneration = new CodeGenerationService(api);
  }

  list(
    params?: IListRedeemablesParams,
  ): UseQueryResult<IListRedeemables, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined || value !== null,
      ),
    );

    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "list"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IListRedeemables>>(
          "/promotions/all/",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: updatedParams,
          },
        );
        return data;
      },
    });
  }

  partialUpdateCode(
    listParams?: IListRedeemablesParams,
  ): UseMutationResult<
    IDiscount | IVoucher | ITab,
    Error,
    IPartialUpdateRedeemableParams
  > {
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams).filter(
        ([, value]) => value !== undefined || value !== null,
      ),
    );
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "partial-code-update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, redeemable_classification, ...restParams } = params;
        const endpointLookup = {
          [RedeemableClassification.Discount]: `/promotions/discount_codes/${id}/`,
          [RedeemableClassification.Voucher]: `/promotions/vouchers/${id}/`,
          [RedeemableClassification.PrepaidTab]: `/promotions/tabs/${id}/`,
        };

        const { data } = await this.api.patch<
          AxiosResponse<IDiscount | IVoucher | ITab>
        >(endpointLookup[redeemable_classification], {
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
          params: restParams,
        });
        return data;
      },
      onMutate: async (params) => {
        const { id, ...restParams } = params;
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
        const previousData = await queryClient.getQueryData<IListRedeemables>(
          buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        );

        if (previousData) {
          await queryClient.setQueryData<IListRedeemables>(
            buildQueryKey({
              queryKey: ["redeemables", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            (old) => {
              return {
                ...old,
                results: old.results.map((result) =>
                  result.id === id ? { ...result, ...restParams } : result,
                ),
              };
            },
          );
        }
        return { previousData };
      },
      onError: (err, params, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            context.previousData,
          );
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }

  deleteCode(
    listParams?: IListRedeemablesParams,
  ): UseMutationResult<boolean, Error, IDeleteRedeemableParams> {
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams).filter(
        ([, value]) => value !== undefined || value !== null,
      ),
    );
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "delete-code"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, redeemable_classification } = params;
        const endpointLookup = {
          [RedeemableClassification.Discount]: `/promotions/discount_codes/${id}/`,
          [RedeemableClassification.Voucher]: `/promotions/vouchers/${id}/`,
          [RedeemableClassification.PrepaidTab]: `/promotions/tabs/${id}/`,
        };
        const { data } = await this.api.delete<AxiosResponse<string>>(
          endpointLookup[redeemable_classification],
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data === "";
      },
      onMutate: async (params) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
        const previousData = await queryClient.getQueryData<IListRedeemables>(
          buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        );

        if (previousData) {
          await queryClient.setQueryData<IListRedeemables>(
            buildQueryKey({
              queryKey: ["redeemables", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            (old) => {
              return {
                ...old,
                results: old.results.filter(
                  (result) => result.id !== params.id,
                ),
              };
            },
          );
        }
        return { previousData };
      },
      onError: (err, params, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            context.previousData,
          );
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }

  exportCodesAsCsv({
    listParams,
    enabled,
  }: {
    listParams: IListRedeemablesParams;
    enabled: boolean;
  }): UseQueryResult<string, Error> {
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams).filter(
        ([, value]) => value !== undefined || value !== null,
      ),
    );

    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "export-codes"],
        venueID: this.api.selectedVenue,
        params: updatedListParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<string>>(
          "/promotions/all/csv",
          {
            params: updatedListParams,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      enabled,
    });
  }

  bulkSendEmail(): UseMutationResult<
    Array<IBulkSendEmailResult>,
    Error,
    IBulkSendEmailParams
  > {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "bulk-send-email"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<
          AxiosResponse<Array<IBulkSendEmailResult>>
        >("/promotions/redeemables/bulk_send_email/", {
          params,
          endpointService: EndpointService.Gateway,
          venueSpecific: false,
        });
        return data;
      },
    });
  }
}
